import React, {useState, useEffect} from 'react';
import {getTrackingId} from '../utils/tracking';
import {useSlotLease} from '../hooks/useSlotLease';

export default function DebugPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(
    sessionStorage.getItem('tracking_debugger_unlocked') === 'true'
  );
  const [trackingId, setTrackingId] = useState('');
  const [mockEnabled, setMockEnabled] = useState(false);
  const {slot, loading, error, skipped} = useSlotLease();

  useEffect(() => {
    setTrackingId(getTrackingId());
    setMockEnabled(localStorage.getItem('glitchy_mock_backend') === 'true');
  }, [isOpen]);

  const unlockDebugger = () => {
    if (isAuthorized) {
      setIsOpen(true);
      return;
    }

    const password = window.prompt('Enter tracking debugger password:');

    if (password === 'nofear') {
      sessionStorage.setItem('tracking_debugger_unlocked', 'true');
      setIsAuthorized(true);
      setIsOpen(true);
    }
  };

  const handleSimulateClick = () => {
    sessionStorage.removeItem('glitchy_dynamic_slot');
    const testId = 'TEST_' + Math.random().toString(36).substr(2, 9).toUpperCase();
    window.location.href = `/?transaction_id=${testId}`;
  };

  const handleClearData = () => {
    localStorage.removeItem('glitchy_tx_id');
    sessionStorage.removeItem('glitchy_dynamic_slot');
    document.cookie = 'glitchy_tx_id=;expires=Thu,01 Jan 1970 00:00:00 UTC;path=/;';
    window.location.href = '/';
  };

  const toggleMockBackend = () => {
    const newVal = !mockEnabled;
    setMockEnabled(newVal);
    localStorage.setItem('glitchy_mock_backend', newVal.toString());
    sessionStorage.removeItem('glitchy_dynamic_slot');
    window.location.reload();
  };

  if (!isOpen) {
    return (
      <button
        aria-label="Open tracking debugger"
        className="fixed bottom-0 left-0 z-50 h-3 w-3 cursor-default bg-transparent p-0 opacity-0"
        onClick={unlockDebugger}
      />
    );
  }

  return (
    <div className="fixed bottom-4 left-4 z-50 w-80 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden font-mono text-sm">
      <div className="bg-slate-800 text-white px-4 py-3 flex justify-between items-center">
        <h3 className="font-bold flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
          Tracking Debugger
        </h3>
        <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-white">
          ✕
        </button>
      </div>

      <div className="p-4 space-y-4">
        <div>
          <p className="text-slate-500 text-xs uppercase font-bold mb-1">
            Current Tracking ID
          </p>
          <div className="bg-slate-100 p-2 rounded border border-slate-200 break-all text-teal-700 font-semibold">
            {trackingId}
          </div>
          {trackingId === 'direct_organic' && (
            <p className="text-xs text-amber-600 mt-1">⚠️ Default fallback active</p>
          )}
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <p className="text-slate-500 text-xs uppercase font-bold">Leased Slot</p>
            <label className="flex items-center gap-1 text-[10px] cursor-pointer">
              <input
                type="checkbox"
                checked={mockEnabled}
                onChange={toggleMockBackend}
                className="accent-teal-600"
              />
              Mock Backend
            </label>
          </div>

          <div className="bg-slate-100 p-2 rounded border border-slate-200 flex flex-col gap-1">
            {loading ? (
              <span className="text-slate-400 animate-pulse">Fetching slot...</span>
            ) : skipped ? (
              <span className="text-amber-600 font-bold">
                Skipped (direct/organic — no slot leased)
              </span>
            ) : slot ? (
              <span className="text-teal-700 font-bold text-lg">#{slot}</span>
            ) : (
              <span className="text-red-500 font-bold">Failed to fetch</span>
            )}

            {error && !loading && (
              <span className="text-[10px] text-red-400 break-all leading-tight">
                {error}
              </span>
            )}
          </div>

          {sessionStorage.getItem('glitchy_dynamic_slot') && !skipped && (
            <p className="text-xs text-green-600 mt-1">
              ✓ Loaded from session cache
            </p>
          )}
        </div>

        <hr className="border-slate-100" />

        <div className="flex flex-col gap-2">
          <button
            onClick={handleSimulateClick}
            className="w-full bg-teal-50 hover:bg-teal-100 text-teal-700 py-2 px-4 rounded border border-teal-200 transition-colors text-center font-semibold"
          >
            Simulate Affiliate Click
          </button>
          <button
            onClick={handleClearData}
            className="w-full bg-red-50 hover:bg-red-100 text-red-600 py-2 px-4 rounded border border-red-200 transition-colors text-center font-semibold"
          >
            Clear Caches & Reset
          </button>
        </div>
      </div>
    </div>
  );
}